import React, { useCallback, useEffect, useId, useMemo, useRef } from "react";
import { activateModal } from "@app/scripts/utils/modal";
import { CartCollection } from "./CartCollection";
import { useShopifyCart } from "@app/scripts/react-hooks/shopify";
import { Button } from "@app/scripts/react-general-components/Button";

type Props = Pick<
  ReturnType<typeof useShopifyCart>,
  "goToCheckout" | "giftsInCart" | "overGiftThreshold" | "gifts" | "giftIds"
> & {
  isOpen: boolean;
  giftPopupHeading?: string;
  setIsOpen: (val: boolean) => void;
};

export const CartGiftPopup: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  giftIds,
  giftsInCart,
  overGiftThreshold,
  goToCheckout,
  gifts,
  giftPopupHeading = "Don't forget to add your free gift!",
}) => {
  const requiresSelection = gifts?.length > 1 || gifts?.some((gift) => gift.variants?.length ?? 0 > 1);
  const shouldDisplay = giftsInCart.length === 0 && requiresSelection && overGiftThreshold;

  const dialog = useRef<HTMLDivElement>(null);
  const title = useRef<HTMLHeadingElement>(null);
  const titleId = useId();
  const closePopup = useCallback(() => {
    setIsOpen(false);
    sessionStorage.setItem("giftPopupClosed", "true");
  }, [setIsOpen]);

  useEffect(() => {
    if (!shouldDisplay || !isOpen || !dialog.current || !title.current) return;
    return activateModal(dialog.current, title.current, closePopup, document.querySelector<HTMLElement>("[data-togglecart]"));
  }, [shouldDisplay, isOpen, closePopup]);

  const visibleGiftIds = useMemo(() => gifts?.filter(g => !g?.hide_in_carousel)?.map(p => p.id), [gifts])

  return shouldDisplay && isOpen ? (
    <div className="geolocation__popup-wrap text-brand-dark-grey">
      <div ref={dialog} id={`${titleId}-dialog`} className="geolocation__popup" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
        <div className="geolocation__popup-inner" style={{ overflow: "hidden" }}>
          <h2 ref={title} id={titleId} tabIndex={-1} className="h1 bold">{giftPopupHeading}</h2>
          <div className="text-left smaller-md-items smaller-lg-items w-full">
            <CartCollection
              productIds={visibleGiftIds}
              disabledDesktop={false}
              disabledMobile={false}
              columnsDesktop={2}
              columnsMobile={1}
              hasQuickAddDrawer={false}
              active
              addToCartBtns
              addAsGift
            />
          </div>
          <Button onClick={closePopup} label={"Close"} variant="text" />
          <Button onClick={goToCheckout} label="Checkout" className="fullwidth" />
        </div>
      </div>
    </div>
  ) : null;
};
